import { api, type DefectsOverview } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { html } from '../ui';

/**
 * Raising a defect.
 *
 * One implementation, opened from the fleet-wide register and from an
 * aircraft's own defects tab. A defect noticed in flight is raised with the
 * tech log entry instead — same record, reached from the cockpit rather than
 * the office.
 */
export function raiseDefect(
  o: DefectsOverview,
  preselectAircraftId: string | null,
  reload: () => void,
): void {
  let severity = 'DEFERRABLE';

  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Raise defect',
    sub: 'Found on the ground. A defect noticed in flight is raised with the tech log entry instead',
    primary: 'Raise defect',
    body: () => html`
      <div class="frow3">
        ${field('Aircraft', select('aircraftId',
          o.aircraft.map((a) => opt(a.id, `${a.registration}${a.status === 'GROUNDED' ? ' — grounded' : ''}`, a.id === preselectAircraftId)).join('')))}
        ${field('Severity', select('severity', [
          opt('DEFERRABLE', 'Deferrable — may be carried under the MEL', severity === 'DEFERRABLE'),
          opt('GROUNDING', 'Grounding — the aircraft does not fly', severity === 'GROUNDING'),
          opt('ADVISORY', 'Advisory — no operational effect', severity === 'ADVISORY'),
        ].join(''), 'data-severity'))}
        ${field('ATA chapter', input('ataChapter', '', 'placeholder="33" maxlength="8"'),
          'Used to spot the same thing recurring')}
      </div>
      ${field('Defect', input('description', '', 'placeholder="Landing light intermittent" maxlength="2000"'))}
      ${field('Raised by', select('raisedByPersonId',
        o.people.map((p) => opt(p.id, `${p.displayName}${p.jobTitle ? ` — ${p.jobTitle}` : ''}`)).join('')))}

      ${severity === 'DEFERRABLE'
        ? html`
            <div class="fset">
              <div class="lg">Carry under the MEL</div>
              <div class="frow">
                ${field('MEL reference', input('melReference', '', 'placeholder="33-40-01a" style="font-family:var(--mono)" maxlength="40"'))}
                ${field('Category', select('melCategory', [
                  opt('', 'Not deferred yet'),
                  ...o.melCategories.map((c) => opt(c.category, c.label)),
                ].join('')), 'The category fixes the rectification interval')}
              </div>
              ${field('Operational limitation', input('operationalLimitation', '', 'placeholder="Day VFR only" maxlength="400"'))}
            </div>
          `
        : severity === 'GROUNDING'
          ? callout('exceed', '✕', html`
              The aircraft will be grounded as soon as this is raised, and no tech log entry can be filed
              against it until the defect is rectified through a work pack.
            `)
          : callout('info', 'i', html`
              An advisory defect is recorded but does not restrict operation and carries no rectification
              interval.
            `)}
    `,
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-severity]')?.addEventListener('change', (e) => {
        severity = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => {
      const description = val(root, 'description');
      if (!description) return { error: 'Say what the defect is.' };
      return {
        aircraftId: val(root, 'aircraftId'),
        description,
        severity: val(root, 'severity'),
        ataChapter: val(root, 'ataChapter') || undefined,
        raisedByPersonId: val(root, 'raisedByPersonId') || undefined,
        melReference: val(root, 'melReference') || undefined,
        melCategory: val(root, 'melCategory') || undefined,
        operationalLimitation: val(root, 'operationalLimitation') || undefined,
      };
    },
    submit: (payload) => api.defects.raise(payload),
    done: (r) =>
      r.aircraftStatus === 'GROUNDED'
        ? `${r.reference} raised — ${r.registration} is now grounded`
        : `${r.reference} raised on ${r.registration}`,
    onDone: reload,
  });
}

/** Opens it for one aircraft, fetching the context first. */
export async function raiseDefectFor(aircraftId: string, reload: () => void): Promise<void> {
  const o = await api.defects.overview();
  raiseDefect(o, aircraftId, reload);
}
